import { lazy, Suspense, useCallback, useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from "react";
import {
  Bot,
  Check,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  Eye,
  LoaderCircle,
  PenLine,
  RefreshCw,
  Send,
  Sparkles,
  Square,
  Wrench,
  X,
} from "lucide-react";
import type { ChewCodeTarget } from "./ChewMarkdown";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  buildFileChatContext,
  buildFileChatSource,
  fileChatAssistantMeta,
  fileChatAssistants,
  fileChatModeMeta,
  fileChatModes,
  loadFileChatConnection,
  fileChatUpdateAvailable,
  loadFileChatModels,
  moveFileChat,
  persistFileChatMode,
  persistFileChatPairing,
  storedFileChatPairing,
  persistFileChatModels,
  persistFileChatSize,
  persistFileChatAssistant,
  resizeFileChat,
  storedFileChatMode,
  storedFileChatModels,
  storedFileChatSize,
  storedFileChatAssistant,
  streamFileChat,
  type FileChatAssistant,
  type FileChatCapability,
  type FileChatContextInput,
  type FileChatMessage,
  type FileChatMode,
  type FileChatOffset,
  type FileChatResizeDirection,
  type FileChatSize,
} from "../lib/file-chat";
import type { FileChatActivity } from "../lib/file-chat-protocol";

const ChewMarkdown = lazy(() => import("./ChewMarkdown").then((module) => ({ default: module.ChewMarkdown })));

type ChatEntry = FileChatMessage & {
  id: string;
  status?: string;
  activities?: FileChatActivity[];
};
const resizeDirections: FileChatResizeDirection[] = ["n", "ne", "e", "se", "s", "sw", "w", "nw"];

export function FileChatPopup(props: FileChatContextInput & { onSelectCode: (target: ChewCodeTarget) => void }) {
  const { path } = props;
  const [open, setOpen] = useState(false);
  const [assistant, setAssistant] = useState<FileChatAssistant>(storedFileChatAssistant);
  const [mode, setMode] = useState<FileChatMode>(storedFileChatMode);
  const [models, setModels] = useState(storedFileChatModels);
  const [pairing, setPairing] = useState(storedFileChatPairing);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [showSetup, setShowSetup] = useState(() => !storedFileChatPairing());
  const [capabilities, setCapabilities] = useState<FileChatCapability[]>();
  const [capabilityError, setCapabilityError] = useState<string>();
  const [capabilitiesLoading, setCapabilitiesLoading] = useState(false);
  const [modelsLoading, setModelsLoading] = useState<FileChatAssistant>();
  const [modelError, setModelError] = useState<string>();
  const [size, setSize] = useState<FileChatSize>(storedFileChatSize);
  const [offset, setOffset] = useState<FileChatOffset>({ x: 0, y: 0 });
  const [interacting, setInteracting] = useState<"drag" | "resize">();
  const [entries, setEntries] = useState<ChatEntry[]>([]);
  const [draft, setDraft] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string>();
  const abortRef = useRef<AbortController>(undefined);
  const modelLoadsRef = useRef(new Set<FileChatAssistant>());
  const activeEntryIdRef = useRef<string>(undefined);
  const scrollRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const resizeCleanupRef = useRef<() => void>(undefined);
  const fileName = path.split("/").at(-1) ?? path;

  useEffect(() => () => {
    abortRef.current?.abort();
    resizeCleanupRef.current?.();
  }, []);
  useEffect(() => {
    const clamp = () => setSize((current) => {
      const next = storedFileChatSize();
      return { width: Math.min(current.width, next.width), height: Math.min(current.height, next.height) };
    });
    const resetPosition = () => setOffset({ x: 0, y: 0 });
    window.addEventListener("resize", clamp);
    window.addEventListener("resize", resetPosition);
    return () => {
      window.removeEventListener("resize", clamp);
      window.removeEventListener("resize", resetPosition);
    };
  }, []);
  useEffect(() => {
    const element = popoverRef.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      // A closing popup shrinks as it animates out. Measuring that would feed a
      // collapsed size back into the width it drives, ratcheting the window
      // smaller on every open.
      if (!entry || element.hasAttribute("data-closed") || element.hasAttribute("data-ending-style")) return;
      const next = {
        width: Math.round(entry.contentRect.width),
        height: Math.round(entry.contentRect.height),
      };
      setSize(next);
      persistFileChatSize(next);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [open]);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [entries, streaming]);

  const chooseAssistant = useCallback((next: FileChatAssistant) => {
    setAssistant(next);
    persistFileChatAssistant(next);
  }, []);

  const chooseMode = useCallback((next: FileChatMode) => {
    setMode(next);
    persistFileChatMode(next);
  }, []);

  const refreshCapabilities = useCallback(async () => {
    if (!storedFileChatPairing()) { setShowSetup(true); return; }
    modelLoadsRef.current.clear();
    setCapabilitiesLoading(true);
    setUpdateAvailable(false);
    setCapabilityError(undefined);
    try {
      const connection = await loadFileChatConnection();
      setCapabilities(connection.assistants);
      setShowSetup(false);
      setUpdateAvailable(await fileChatUpdateAvailable(connection.bridgeVersion));
    } catch (reason) {
      setCapabilities(undefined);
      setShowSetup(true);
      setCapabilityError(reason instanceof Error ? reason.message : "Could not reach the local chat companion.");
    } finally {
      setCapabilitiesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) void refreshCapabilities();
  }, [open, refreshCapabilities]);

  const ensureModels = useCallback(async (target: FileChatAssistant) => {
    const capability = capabilities?.find((item) => item.id === target);
    if (!capability?.installed || capability.models.length || modelLoadsRef.current.has(target)) return;
    modelLoadsRef.current.add(target);
    setModelsLoading(target);
    setModelError(undefined);
    try {
      const discovered = await loadFileChatModels(target);
      setCapabilities((current) => current?.map((item) =>
        item.id === target ? { ...item, ...discovered } : item
      ));
      if (discovered.discovery.error && !discovered.models.length) setModelError(discovered.discovery.error);
    } catch (reason) {
      setModelError(reason instanceof Error ? reason.message : "Could not load model options.");
    } finally {
      setModelsLoading((current) => current === target ? undefined : current);
    }
  }, [capabilities]);

  const stop = useCallback(() => {
    const activeEntryId = activeEntryIdRef.current;
    abortRef.current?.abort();
    abortRef.current = undefined;
    activeEntryIdRef.current = undefined;
    if (activeEntryId) {
      setEntries((current) => current.filter((entry) => entry.id !== activeEntryId || entry.content));
    }
    setStreaming(false);
  }, []);

  const beginResize = useCallback((direction: FileChatResizeDirection, event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    resizeCleanupRef.current?.();
    const pointerId = event.pointerId;
    const origin = { x: event.clientX, y: event.clientY };
    const initialSize = size;
    const initialOffset = offset;
    let latestSize = initialSize;

    const move = (moveEvent: PointerEvent) => {
      if (moveEvent.pointerId !== pointerId) return;
      latestSize = resizeFileChat(initialSize, direction, {
        x: moveEvent.clientX - origin.x,
        y: moveEvent.clientY - origin.y,
      });
      setSize(latestSize);
      setOffset({
        x: initialOffset.x + (direction.includes("e") ? latestSize.width - initialSize.width : 0),
        y: initialOffset.y + (direction.includes("n") ? initialSize.height - latestSize.height : 0),
      });
    };
    const cleanup = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", finish);
      resizeCleanupRef.current = undefined;
      setInteracting(undefined);
    };
    const finish = (finishEvent: PointerEvent) => {
      if (finishEvent.pointerId !== pointerId) return;
      persistFileChatSize(latestSize);
      cleanup();
    };
    setInteracting("resize");
    resizeCleanupRef.current = cleanup;
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", finish);
  }, [offset, size]);

  /**
   * Drag from the header. The popover is anchored to its trigger, so movement
   * is a translation away from that anchor rather than an absolute position.
   */
  const beginDrag = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const element = popoverRef.current;
    if (!element || event.button !== 0) return;
    if ((event.target as HTMLElement).closest("button, select, input, textarea, a")) return;
    event.preventDefault();
    resizeCleanupRef.current?.();
    const pointerId = event.pointerId;
    const origin = { x: event.clientX, y: event.clientY };
    const initialOffset = offset;
    const rect = element.getBoundingClientRect();
    const frame = {
      left: rect.left - initialOffset.x,
      top: rect.top - initialOffset.y,
      width: rect.width,
      height: rect.height,
    };

    const move = (moveEvent: PointerEvent) => {
      if (moveEvent.pointerId !== pointerId) return;
      setOffset(moveFileChat(initialOffset, {
        x: moveEvent.clientX - origin.x,
        y: moveEvent.clientY - origin.y,
      }, frame));
    };
    const cleanup = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", finish);
      resizeCleanupRef.current = undefined;
      setInteracting(undefined);
    };
    const finish = (finishEvent: PointerEvent) => {
      if (finishEvent.pointerId !== pointerId) return;
      cleanup();
    };
    setInteracting("drag");
    resizeCleanupRef.current = cleanup;
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", finish);
  }, [offset]);

  const send = useCallback(async () => {
    const question = draft.trim();
    if (!question || streaming) return;
    setError(undefined);
    setDraft("");
    const history: FileChatMessage[] = [
      ...entries.map((entry) => ({
        role: entry.role,
        content: entry.content,
        assistant: entry.assistant,
        model: entry.model,
      })),
      { role: "user", content: question },
    ];
    const responseAssistant = assistant;
    const responseModel = models[assistant]?.trim() || undefined;
    const assistantId = crypto.randomUUID();
    activeEntryIdRef.current = assistantId;
    setEntries((current) => [
      ...current,
      { id: crypto.randomUUID(), role: "user", content: question },
      {
        id: assistantId,
        role: "assistant",
        assistant: responseAssistant,
        model: responseModel,
        content: "",
        status: `Starting ${fileChatAssistantMeta[responseAssistant].label}`,
        activities: [],
      },
    ]);
    setStreaming(true);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const context = buildFileChatContext(props);
      await streamFileChat({
        assistant: responseAssistant,
        model: responseModel,
        context,
        source: buildFileChatSource(props),
        mode,
        messages: history,
        signal: controller.signal,
        onDelta: (delta) => {
          setEntries((current) =>
            current.map((entry) =>
              entry.id === assistantId
                ? { ...entry, content: entry.content + delta, status: "Responding" }
                : entry,
            ),
          );
        },
        onStatus: (status) => {
          setEntries((current) =>
            current.map((entry) => entry.id === assistantId ? { ...entry, status } : entry),
          );
        },
        onActivity: (activity) => {
          setEntries((current) =>
            current.map((entry) => {
              if (entry.id !== assistantId) return entry;
              const activities = entry.activities ?? [];
              const existing = activities.findIndex((item) => item.id === activity.id);
              return {
                ...entry,
                activities: existing < 0
                  ? [...activities, activity]
                  : activities.map((item, index) => index === existing ? activity : item),
              };
            }),
          );
        },
      });
    } catch (reason) {
      if (controller.signal.aborted) {
        setEntries((current) => current.filter((entry) => entry.id !== assistantId || entry.content));
        return;
      }
      setError(reason instanceof Error ? reason.message : "The review assistant could not respond.");
      setEntries((current) => current.filter((entry) => entry.id !== assistantId || entry.content));
    } finally {
      if (abortRef.current === controller) abortRef.current = undefined;
      if (activeEntryIdRef.current === assistantId) activeEntryIdRef.current = undefined;
      setEntries((current) =>
        current.map((entry) => entry.id === assistantId
          ? {
              ...entry,
              activities: entry.activities?.map((activity) =>
                activity.status === "active" ? { ...activity, status: "complete" } : activity,
              ),
            }
          : entry),
      );
      setStreaming(false);
    }
  }, [assistant, draft, entries, mode, models, props, streaming]);

  const selectedCapability = capabilities?.find((capability) => capability.id === assistant);
  const status = capabilitiesLoading
    ? { className: "loading", icon: <LoaderCircle aria-hidden="true" />, text: `Checking local ${fileChatAssistantMeta[assistant].label} CLI…` }
    : capabilityError
      ? { className: "error", icon: <CircleAlert aria-hidden="true" />, text: "Local bridge is not connected" }
      : selectedCapability?.installed
        ? {
            className: selectedCapability.discovery.error ? "warning" : "connected",
            icon: selectedCapability.discovery.error ? <CircleAlert aria-hidden="true" /> : <Check aria-hidden="true" />,
            text: `Connected to local ${selectedCapability.label} CLI${selectedCapability.version ? ` · ${selectedCapability.version}` : ""}`,
          }
        : { className: "error", icon: <CircleAlert aria-hidden="true" />, text: `${fileChatAssistantMeta[assistant].label} CLI is not installed` };

  useEffect(() => {
    if (open && selectedCapability?.installed && !selectedCapability.models.length) {
      void ensureModels(assistant);
    }
  }, [assistant, ensureModels, open, selectedCapability]);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setSize(storedFileChatSize());
          setOffset({ x: 0, y: 0 });
        }
        setOpen(next);
        if (!next) stop();
      }}
    >
      <Tooltip>
        <TooltipTrigger
          render={
            <PopoverTrigger
              render={
                <Button
                  aria-label={`Ask ${fileChatAssistantMeta[assistant].label} about ${fileName}`}
                  className="file-chat-trigger"
                  size="icon-xs"
                  type="button"
                  variant="ghost"
                />
              }
            />
          }
        >
          <Sparkles aria-hidden="true" />
        </TooltipTrigger>
        <TooltipContent side="bottom" sideOffset={5}>Ask AI about this file</TooltipContent>
      </Tooltip>
      <PopoverContent
        align="end"
        className="file-chat-popover"
        data-interacting={interacting}
        ref={popoverRef}
        side="bottom"
        sideOffset={6}
        style={{
          "--file-chat-width": `${size.width}px`,
          "--file-chat-height": `${size.height}px`,
          "--file-chat-offset-x": `${offset.x}px`,
          "--file-chat-offset-y": `${offset.y}px`,
        } as CSSProperties}
      >
        {resizeDirections.map((direction) => (
          <div
            aria-hidden="true"
            className={`file-chat-resize-handle file-chat-resize-${direction}`}
            key={direction}
            onPointerDown={(event) => beginResize(direction, event)}
          />
        ))}
        <header className="file-chat-header" data-dragging={interacting === "drag" || undefined} onPointerDown={beginDrag}>
          <span className="file-chat-title"><Bot aria-hidden="true" /><strong>{showSetup ? "Connect your assistant" : `Ask about ${fileName}`}</strong></span>
          <span className="file-chat-beta" title="File chat is in beta">Beta</span>
          {!showSetup && <div aria-label="Choose assistant" className="file-chat-assistants" role="radiogroup">
            {fileChatAssistants.map((id) => (
              <button
                aria-checked={assistant === id}
                className={assistant === id ? "active" : undefined}
                key={id}
                disabled={streaming}
                onClick={() => chooseAssistant(id)}
                role="radio"
                type="button"
              >
                {fileChatAssistantMeta[id].label}
                {assistant === id && <Check aria-hidden="true" />}
              </button>
            ))}
          </div>}
          {!showSetup && <Button disabled={streaming} onClick={() => setShowSetup(true)} size="sm" variant="ghost">Connection</Button>}
          {showSetup && capabilities && <Button onClick={() => setShowSetup(false)} size="sm" variant="ghost">Back to chat</Button>}
          <button aria-label="Close chat" className="file-chat-close" onClick={() => setOpen(false)} type="button"><X aria-hidden="true" /></button>
        </header>
        {showSetup ? (
          <form style={{ flex: 1, minHeight: 0, overflow: "auto", padding: 20, display: "grid", alignContent: "start", gap: 16 }} onSubmit={(event) => {
            event.preventDefault();
            try { persistFileChatPairing(pairing); void refreshCapabilities(); }
            catch (reason) { setCapabilityError(reason instanceof Error ? reason.message : "Invalid pairing code."); }
          }}>
            <p>Keep CHEW open. You’ll need Node.js 22+, Git, and a signed-in LLM CLI.</p>
            <ol style={{ display: "grid", gap: 16, paddingLeft: 20, listStyle: "decimal" }}>
              <li><a href="/downloads/chew-bridge.mjs" download style={{ textDecoration: "underline" }}>Download the bridge</a>.</li>
              <li>In the download folder, run:
                <pre style={{ marginTop: 8, padding: 10, background: "var(--surface-soft)", borderRadius: 4, overflow: "auto", userSelect: "all" }}><code>node chew-bridge.mjs</code></pre>
                <small style={{ color: "var(--accent-foreground)" }}>Already have a CHEW checkout? Run <code>npm run file-chat</code> instead.</small>
              </li>
              <li>Paste the code from your terminal, then connect.
                <input style={{ display: "block", marginTop: 8, width: "100%", minWidth: 0, height: 36, padding: "4px 8px", border: "1px solid var(--line)", borderRadius: 6, background: "transparent" }} aria-label="Bridge pairing code" placeholder="Pairing code" autoComplete="off" type="password" value={pairing} onChange={(event) => setPairing(event.target.value)} />
              </li>
            </ol>
            {capabilityError && <p role="alert">{capabilityError}</p>}
            <div style={{ display: "flex", gap: 8 }}>
              <Button disabled={capabilitiesLoading || !pairing.trim()} type="submit">{capabilitiesLoading ? "Connecting…" : "Connect"}</Button>
              {capabilities && <Button variant="outline" type="button" onClick={() => {
                persistFileChatPairing(""); setPairing(""); setCapabilities(undefined); setCapabilityError(undefined);
              }}>Disconnect</Button>}
            </div>
            <small>Allow local network access if asked. Keep the terminal open while chatting.</small>
          </form>
        ) : (<>
        <div aria-live="polite" className={`file-chat-status ${status.className}`} role="status">
          {status.icon}<span>{status.text}</span>
          <button aria-label="Refresh local CLI capabilities" disabled={capabilitiesLoading} onClick={() => void refreshCapabilities()} type="button">
            <RefreshCw aria-hidden="true" />
          </button>
        </div>
        {updateAvailable && <div role="status" style={{ padding: "8px 12px", background: "var(--accent)", color: "var(--accent-foreground)", fontSize: 12 }}>
          <strong>Bridge update available.</strong>{" "}<a href="/downloads/chew-bridge.mjs" download style={{ textDecoration: "underline" }}>Download update</a>
          <p>When you’re done chatting: stop the bridge, replace the file, restart it, and paste the new pairing code in Connection.</p>
        </div>}
        <label className="file-chat-model">
          <span>Model</span>
          <select
            disabled={streaming || !selectedCapability?.installed}
            onFocus={() => void ensureModels(assistant)}
            onPointerDown={() => void ensureModels(assistant)}
            onChange={(event) => {
              const next = { ...models, [assistant]: event.target.value };
              setModels(next);
              persistFileChatModels(next);
            }}
            value={models[assistant] ?? ""}
          >
            <option value="">{selectedCapability?.defaultModel.label ?? "Default (loading…)"}</option>
            {models[assistant] && !selectedCapability?.models.some((model) => model.id === models[assistant]) && (
              <option value={models[assistant]}>Previously selected ({models[assistant]})</option>
            )}
            {selectedCapability?.models.map((model) => <option key={model.id} value={model.id}>{model.label}</option>)}
            {modelsLoading === assistant && <option disabled>Loading models…</option>}
          </select>
        </label>
        {modelError && <p className="file-chat-model-error" role="alert">{modelError}</p>}
        <div className="file-chat-model file-chat-mode">
          <span>Access</span>
          <div aria-label="Choose what the assistant may do with the checkout" className="file-chat-assistants" role="radiogroup">
            {fileChatModes.map((id) => (
              <button
                aria-checked={mode === id}
                className={mode === id ? "active" : undefined}
                disabled={streaming}
                key={id}
                onClick={() => chooseMode(id)}
                role="radio"
                title={fileChatModeMeta[id].hint}
                type="button"
              >
                {id === "workspace" ? <PenLine aria-hidden="true" /> : <Eye aria-hidden="true" />}
                {fileChatModeMeta[id].label}
              </button>
            ))}
          </div>
        </div>
        <div className="file-chat-transcript" ref={scrollRef}>
          {entries.length === 0 && !streaming ? (
            <p className="file-chat-empty">Ask about this change. The assistant sees the file diff plus the PR description, changed files, comments, and reviewers, and works from a checkout of this commit that it {fileChatModeMeta[mode].hint.charAt(0).toLowerCase()}{fileChatModeMeta[mode].hint.slice(1)}</p>
          ) : (
            entries.map((entry) => (
              <div className={`file-chat-message file-chat-${entry.role}`} key={entry.id}>
                <span className="file-chat-role">
                  {entry.role === "user"
                    ? "You"
                    : `${fileChatAssistantMeta[entry.assistant ?? "codex"].label}${entry.model ? ` · ${entry.model}` : " · CLI default"}`}
                </span>
                {entry.role === "assistant" && entry.activities?.length ? (
                  <details className="file-chat-activity" open>
                    <summary>
                      <ChevronRight aria-hidden="true" />
                      Activity
                      {entry.activities.some((item) => item.status === "active")
                        ? <LoaderCircle aria-hidden="true" className="file-chat-spinner" />
                        : <CircleCheck aria-hidden="true" />}
                    </summary>
                    <ol>
                      {entry.activities.map((item) => (
                        <li className={`file-chat-activity-${item.status}`} key={item.id}>
                          {item.kind === "tool" ? <Wrench aria-hidden="true" /> : <Sparkles aria-hidden="true" />}
                          <span><strong>{item.label}</strong>{item.detail && <small>{item.detail}</small>}</span>
                        </li>
                      ))}
                    </ol>
                  </details>
                ) : null}
                {entry.content ? entry.role === "assistant"
                  ? <Suspense fallback={<p>{entry.content}</p>}><ChewMarkdown body={entry.content} codePaths={Object.keys(props.files.files)} onSelectCode={props.onSelectCode} repository={props.descriptor.repository} /></Suspense>
                  : <p>{entry.content}</p>
                  : streaming && entry.role === "assistant" && activeEntryIdRef.current === entry.id ? (
                  <div aria-live="polite" className="file-chat-thinking" role="status">
                    <span className="file-chat-thinking-orb" />
                    <span>{entry.status ?? "Thinking"}</span>
                    <span aria-hidden="true" className="file-chat-thinking-dots"><i /><i /><i /></span>
                  </div>
                ) : null}
              </div>
            ))
          )}
        </div>
        {error && <p className="file-chat-error" role="alert">{error}</p>}
        <form
          className="file-chat-composer"
          onSubmit={(event) => {
            event.preventDefault();
            void send();
          }}
        >
          <textarea
            aria-label={`Ask ${fileChatAssistantMeta[assistant].label} about ${fileName}`}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                void send();
              }
            }}
            placeholder={`Ask ${fileChatAssistantMeta[assistant].label}…`}
            rows={2}
            value={draft}
          />
          {streaming ? (
            <Button aria-label="Stop response" onClick={stop} size="icon-xs" type="button" variant="ghost"><Square aria-hidden="true" /></Button>
          ) : (
            <Button aria-label="Send message" disabled={!draft.trim()} size="icon-xs" type="submit" variant="ghost"><Send aria-hidden="true" /></Button>
          )}
        </form>
        </>)}
      </PopoverContent>
    </Popover>
  );
}
